import { useQuery } from '@apollo/client';
import { ErrorOutline } from '@mui/icons-material';
import {
    Box, Button,
    CardActions,
    CardContent,
    CardMedia,
    CircularProgress,
    Container,
    Dialog,
    Divider,
    Grid,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { BsFillFileEarmarkZipFill } from 'react-icons/bs';
import { FaCheckCircle } from 'react-icons/fa';
import { MdCloudUpload, MdEmail } from 'react-icons/md';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import Nav, { USER_DATA } from '../Components/Settings/Nav';
import { CONFIG_APP_UPTIME_METRICS_BASE_URL } from '../configApp';

const faqData = [
    {
        question: "📌 What is Trackify?",
        answer: "Trackify is a smart tool that helps you track and analyze content from Notion docs, YouTube videos, and zip files using AI."
    },
    {
        question: "📤 What files and links do I need to upload?",
        answer: "You need to upload a Notion document URL, an unlisted YouTube video URL, and a .zip file related to your content."
    },
    {
        question: "💡 What happens after I upload my data?",
        answer: "Your data is sent to our AI engine for validation and analysis. This typically takes a few seconds, and you’ll get a confirmation once done."
    },
    {
        question: "🛡️ Is my data secure?",
        answer: "Yes, we take data privacy seriously. All your uploads are securely processed and not shared externally."
    },
    {
        question: "🧠 What insights will I receive?",
        answer: "You’ll get AI-powered insights extracted from your uploaded content — helping you understand patterns, quality, and engagement potential."
    },
    {
        question: "❓ Having issues uploading?",
        answer: "Make sure your links are correct and the file is under the size limit. Still stuck? Reach out to support for help!"
    }
];


const Trackify = () => {
    const { enqueueSnackbar } = useSnackbar();
    let history = useHistory();
    const { error: err, data } = useQuery(USER_DATA);
    const [email, setEmail] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [image, setImage] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [notionUrl, setNotionUrl] = useState('https://launch.nife.io/Trackify');
    const [youtubeUrl, setYoutubeUrl] = useState('https://launch.nife.io/Trackify');
    const [dialogOpen, setDialogOpen] = useState(false);

    useEffect(() => {
        if (data) {
            setEmail(data.getUserById.email);
        }
    }, [data]);


    const handleDrop = (acceptedFiles) => {
        setImage(acceptedFiles[0]);
        setErrorMessage('');
    };

    // const uploadInChunks = async (file, email, notionUrl, youtubeUrl, userId) => {
    //     const chunkSize = 5 * 1024 * 1024; // 5MB
    //     const totalChunks = Math.ceil(file.size / chunkSize);
    //     const fileId = `${userId}-${Date.now()}`; // Unique ID to group chunks

    //     for (let index = 0; index < totalChunks; index++) {
    //         const start = index * chunkSize;
    //         const end = Math.min(file.size, start + chunkSize);
    //         const chunk = file.slice(start, end);

    //         const formData = new FormData();
    //         formData.append('chunk', chunk);
    //         formData.append('index', index);
    //         formData.append('totalChunks', totalChunks);
    //         formData.append('fileId', fileId);
    //         formData.append('email', email);
    //         formData.append('userId', userId);
    //         formData.append('notionUrl', notionUrl);
    //         formData.append('youtubeVideoUrl', youtubeUrl);

    //         await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/trackify-upload-chunk`, formData);
    //     }

    //     // Notify server to assemble
    //     await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/trackify-upload-complete`, {
    //         fileId,
    //         email,
    //         userId,
    //         notionUrl,
    //         youtubeUrl
    //     });
    // };


 const handleUpload = async (e) => {
    e.preventDefault();
    setDialogOpen(true);

    if (!email || !image || !notionUrl || !youtubeUrl) {
        setErrorMessage('Please fill in all fields: email, Notion URL, YouTube URL, and upload a zip file.');
        setDialogOpen(false);
        return;
    }

    const userId = localStorage.getItem('userId');
    if (!userId) {
        setErrorMessage('User ID is missing. Please ensure you are logged in.');
        setDialogOpen(false);
        return;
    }

    setUploading(true);
    setErrorMessage('');
    const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per chunk
    const totalChunks = Math.ceil(image.size / CHUNK_SIZE);
    const fileId = `${userId}-${Date.now()}`;

    try {
        for (let index = 0; index < totalChunks; index++) {
            const start = index * CHUNK_SIZE;
            const end = Math.min(image.size, start + CHUNK_SIZE);
            const chunk = image.slice(start, end);

            const chunkForm = new FormData();
            chunkForm.append('chunk', chunk);
            chunkForm.append('index', index);
            chunkForm.append('totalChunks', totalChunks);
            chunkForm.append('fileId', fileId);
            chunkForm.append('email', email);
            chunkForm.append('userId', userId);
            chunkForm.append('notionUrl', notionUrl);
            chunkForm.append('youtubeVideoUrl', youtubeUrl);

            // await axios.post(`http://localhost:5000/trackify-upload-chunk`, chunkForm);
              await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/trackify-upload-chunk`, chunkForm);
        }

        // After all chunks are sent, notify the server to combine them and upload to S3
           const finalizeRes = await axios.post(`${CONFIG_APP_UPTIME_METRICS_BASE_URL}/trackify-upload-complete`, {
        // const finalizeRes = await axios.post(`http://localhost:5000/trackify-upload-complete`, {
            fileId,
            email,
            userId,
            notionUrl,
            youtubeUrl
        });

        setImageUrl(finalizeRes.data.zipFileUrl);
        setImage(null);

        enqueueSnackbar('[TRACKIFY✨] Upload successful and validated by AI!', {
            variant: 'success',
            autoHideDuration: 5000,
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
        });

    } catch (err) {
        console.error('Chunked upload failed:', err);
        setErrorMessage('Something went wrong during chunked upload. Try again.');
    } finally {
        setUploading(false);
        setDialogOpen(false);
    }
};



    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: '.zip',
        maxFiles: 1,
        multiple: false,
    });



    const handleRemoveImage = () => {
        setImage(null);
        setImageUrl('');
    };

    return (
        <Box sx={{ flexGrow: 1, height: '99.3vh', overflow: 'auto' }}>
            <Nav />

            <Container sx={{ maxHeight: "100vh" }}>
                <Box sx={{ paddingTop: 3, textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: 2, color: 'primary.main' }}>
                        Trackify by Nife
                    </Typography>
                    <Typography variant="h6" sx={{ maxWidth: 800, margin: '0 auto' }}>
                        Trackify is an advanced tracking application powerd with AI, designed to help you monitor activities across various platforms.
                        Upload your zip file to get started.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingBottom: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ width: { xs: '100%', sm: '40%' }, display: 'flex', justifyContent: 'center', marginBottom: { xs: 2, sm: 0 } }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 2, marginTop: 2, backgroundColor: '#f0f0f0', borderRadius: 2 }}>

                            <img
                                src="https://react-nife-2024.s3.ap-south-1.amazonaws.com/oikos-features/trakify-upload.svg"
                                alt="Trackify Hero"
                                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ width: { xs: '100%', sm: '50%' }, paddingLeft: { sm: 5 } }}>


                        <form onSubmit={handleUpload} style={{ width: '100%' }}>
                            {(email || isEditing) ? (
                                <ListItem sx={{ width: 'auto' }}>
                                    <ListItemIcon sx={{ fontSize: "xx-large" }}>
                                        <MdEmail style={{ color: "#160044" }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={email}
                                        secondary="This email will be used for uploading"
                                    />
                                </ListItem>
                            ) : (
                                <Box sx={{ padding: 2 }}>
                                    <Skeleton variant="text" width={"100%"} height={80} sx={{ marginBottom: 2 }} />
                                </Box>
                            )}
                            {/* <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                                <InputLabel sx={{ top: -6, left: 16 }} htmlFor="outlined-adornment-amount">Notion Document URL</InputLabel>
                                <OutlinedInput
                                    label="Notion Document URL"
                                    type="url"
                                    fullWidth
                                    placeholder='https://www.notion.so/your-document-url'
                                    startAdornment={<InputAdornment position="start" sx={{ padding: '6px' }}><SiNotion style={{ fontSize: "30px", color: "black" }} /></InputAdornment>}
                                    margin="normal"
                                    value={notionUrl}
                                    onChange={(e) => setNotionUrl(e.target.value)}
                                    required
                                />
                            </FormControl>
                            <FormControl fullWidth variant="standard" sx={{ mb: 2 }}>
                                <InputLabel sx={{ top: -6, left: 16 }} htmlFor="outlined-adornment-amount">YouTube Unlisted Video URL</InputLabel>
                                <OutlinedInput
                                    label="YouTube Unlisted Video URL"
                                    type="url"
                                    fullWidth
                                    placeholder='https://www.youtube.com/watch?v=your-video-id'
                                    startAdornment={<InputAdornment position="start" sx={{ padding: '6px' }}><FaYoutube style={{ fontSize: "30px", color: "red" }} /></InputAdornment>}
                                    margin="normal"
                                    value={youtubeUrl}
                                    onChange={(e) => setYoutubeUrl(e.target.value)}
                                    required
                                />
                            </FormControl> */}

                            {image ? (
                                <Box
                                    mt={2}
                                    textAlign="center"
                                    sx={{
                                        padding: 2,
                                        borderRadius: 1,
                                        border: '1px solidrgb(161, 161, 161)',
                                        mb: 2,
                                        backgroundColor: 'background.paper',
                                    }}
                                >
                                    <BsFillFileEarmarkZipFill size={40} color="#1976d2" />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1,
                                            color: 'text.primary',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                        }}
                                    >
                                        {image.name}
                                    </Typography>
                                    <Box sx={{ marginTop: 1 }}>
                                        <Button
                                            variant="text"
                                            onClick={handleRemoveImage}
                                            sx={{
                                                fontSize: '12px',

                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 0, 0, 0.1)',
                                                },
                                            }}
                                        >
                                            Remove File
                                        </Button>
                                    </Box>
                                </Box>
                            ) : (
                                <Box
                                    {...getRootProps()}
                                    sx={{
                                        border: '2px dashed #1976d2',
                                        borderRadius: 2,
                                        padding: 3,
                                        height: 200,
                                        textAlign: 'center',
                                        marginBottom: 2,
                                        cursor: 'pointer',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    }}
                                >
                                    <input {...getInputProps({ accept: '.zip' })} />
                                    <MdCloudUpload style={{ fontSize: "3rem", marginTop: "20px" }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Drag & drop a zip file here, or click to select
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                fullWidth
                                disabled={uploading || !image}
                            >
                                {uploading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                            </Button>

                            <Button sx={{ mt: 2 }} onClick={() => history.push("/")} color="primary" fullWidth disabled={false}>Back to Home</Button>
                        </form>

                        {errorMessage && <Grid
                            container
                            direction="row"
                            justifyContent="center"
                            alignItems="center"
                            sx={{ background: "rgb(253, 246, 246)", color: "rgb(211, 47, 47)", p: 3, borderRadius: "12px", mt: 2 }}
                        >

                            <ErrorOutline size={5} color='error' />
                            <Typography sx={{ ml: 1 }}>
                                Upload failed because of heavy traffic. Please try again. {errorMessage}
                            </Typography>
                        </Grid>}


                        {imageUrl && (
                            <Box mt={4} textAlign="center">
                                <Grid container spacing={2} justifyContent="center" alignItems="center">
                                    <FaCheckCircle style={{ color: "green" }} />
                                    <Typography variant="h6">Upload Successful!</Typography> </Grid>

                                {/* <a href={imageUrl} target="_blank" rel="noopener noreferrer">{imageUrl}</a> */}
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* <Box sx={{ padding: 0, pb: 2 }}>
                    <Typography variant="h5" align="center" sx={{ marginBottom: 3 }}>
                        Frequently Asked Questions
                    </Typography>
                    <Grid container spacing={2} justifyContent="center">
                        {faqData.map((faq, index) => (
                            <Grid item xs={12} sm={4} key={index}>
                                <Card sx={{ boxShadow: 1, borderRadius: 3, minHeight: 150 }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                            {faq.question}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {faq.answer}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box> */}


                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
                    <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img
                            src='https://react-nife-2024.s3.ap-south-1.amazonaws.com/searchLeaks.gif'
                            width={"auto"}
                            height={"200px"}
                            alt="AI Validation"
                        />

                        <Typography variant="h5" sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={15} /> Your data is being validated by AI...
                        </Typography>

                        <Divider sx={{ width: '100%', my: 3 }} />

                        <Typography variant="body2" sx={{ mb: 2, fontSize: "1rem", fontWeight: 'bold' }}>
                            While you wait..
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            Check out these cool games and tools we have for you:
                        </Typography>

                        <Grid container spacing={2} justifyContent="center">
                            {/* Wordle Card */}
                            <Grid item xs={12} sm={6}>
                                <Paper variant='outlined' sx={{ borderRadius: "12px" }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image="https://imageirl.imageresizer.io/YasJ5iJVl2-s895x715-q90.jpg"
                                        alt="Wordle"
                                    />
                                    <CardContent>
                                        <Typography variant="h6">Wordle</Typography>
                                        <Typography variant="body2" color="text.secondary">Game</Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <Button onClick={() => history.push(`/deploy?name=Wordle`)} fullWidth size="small" variant="contained">Deploy & play</Button>
                                    </CardActions>
                                </Paper>
                            </Grid>

                            {/* Super Mario Card */}
                            <Grid item xs={12} sm={6}>
                                <Paper variant='outlined' sx={{ borderRadius: "12px" }}>
                                    <CardMedia
                                        component="img"
                                        height="140"
                                        image="https://raw.githubusercontent.com/PengBAI/mariohtml5/master/demo/demo.PNG"
                                        alt="Super Mario"
                                    />
                                    <CardContent>
                                        <Typography variant="h6">Super Mario</Typography>
                                        <Typography variant="body2" color="text.secondary">Game</Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                                        <Button onClick={() => history.push(`/deploy?name=Super Mario`)} fullWidth size="small" variant="contained">Deploy & play</Button>
                                    </CardActions>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                </Dialog>
            </Container>
        </Box>
    );
};

export default Trackify;
